import React, { useState, useEffect, useRef, useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { tokens } from "../../../theme";

/**
 * TrainingAnimation — live federated-learning execution indicator.
 *
 * Replaces the old left-to-right "scanner" loop. This component is now a
 * faithful, at-a-glance status map of a federated round:
 *   decentralized clients train locally  →  push encrypted updates to the
 *   server  →  the server aggregates  →  the refined global model propagates
 *   back out  →  next round.
 *
 * It is DRIVEN BY REAL STATE, not a loop. All values are read-only props the
 * Execute page already holds; this component never calls the backend, mutates
 * context, or owns any data-fetching logic.
 *
 * Props (all optional except isRunning, for back-compat):
 *   isRunning · isPaused · isCompleted · progress · currentRound ·
 *   totalRounds · connectedClients · hasError
 *
 * Derived phases: idle · running · aggregating · complete · failed · paused.
 */

const HUB = { x: 400, y: 110 };

// Lay participating clients out in two columns flanking the server, which
// reads well in this wide/short panel and scales cleanly from 1..8 nodes.
function layoutClients(count) {
  const n = Math.max(0, count);
  const positions = [];
  const leftN = Math.ceil(n / 2);
  const rightN = Math.floor(n / 2);
  const cols = [
    { x: 120, count: leftN },
    { x: 680, count: rightN },
  ];
  cols.forEach((col) => {
    for (let i = 0; i < col.count; i += 1) {
      const t = col.count === 1 ? 0.5 : i / (col.count - 1);
      const y = 34 + t * (186 - 34);
      positions.push({ x: col.x, y });
    }
  });
  return positions;
}

const TrainingAnimation = ({
  isRunning,
  isPaused = false,
  isCompleted = false,
  progress = 0,
  currentRound = 0,
  totalRounds = 0,
  connectedClients = 0,
  hasError = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const prefersReducedMotion = useReducedMotion();

  // ── Visibility: show while running, then briefly linger on a terminal
  //    frame (complete/failed) so the outcome is legible before collapse.
  //    We never show a stale "complete" on mount — only after a live run. ──
  const [visible, setVisible] = useState(Boolean(isRunning));
  const wasRunningRef = useRef(Boolean(isRunning));
  const lingerTimer = useRef(null);

  useEffect(() => {
    const was = wasRunningRef.current;
    wasRunningRef.current = Boolean(isRunning);
    if (isRunning) {
      if (lingerTimer.current) clearTimeout(lingerTimer.current);
      setVisible(true);
      return undefined;
    }
    if (was && (isCompleted || hasError || progress >= 100)) {
      setVisible(true);
      lingerTimer.current = setTimeout(() => setVisible(false), 2800);
    } else if (was) {
      setVisible(false);
    }
    return undefined;
  }, [isRunning, isCompleted, hasError, progress]);

  useEffect(() => () => lingerTimer.current && clearTimeout(lingerTimer.current), []);

  // ── Aggregation pulse: fires the moment a round boundary is crossed.
  //    This is the server aggregation + global-model redistribution step. ──
  const [aggregating, setAggregating] = useState(false);
  const prevRoundRef = useRef(currentRound);
  const aggTimer = useRef(null);
  useEffect(() => {
    if (currentRound > prevRoundRef.current && currentRound > 0 && isRunning) {
      setAggregating(true);
      if (aggTimer.current) clearTimeout(aggTimer.current);
      aggTimer.current = setTimeout(() => setAggregating(false), 1100);
    }
    prevRoundRef.current = currentRound;
    return undefined;
  }, [currentRound, isRunning]);
  useEffect(() => () => aggTimer.current && clearTimeout(aggTimer.current), []);

  // ── Phase derivation (pure function of props/derived flags) ──
  let phase = "idle";
  if (hasError) phase = "failed";
  else if (isCompleted || progress >= 100) phase = "complete";
  else if (isPaused) phase = "paused";
  else if (isRunning) phase = aggregating ? "aggregating" : "running";

  // Live motion runs the whole time an execution is active (including the
  // brief "connecting" window before clients report) so the panel never looks
  // dead. Terminal/paused states settle to a static frame.
  const live = !prefersReducedMotion && (phase === "running" || phase === "aggregating");

  // ── Palette per phase ──
  const accent = {
    running: colors.secondary[500],
    aggregating: colors.amber[500],
    paused: colors.grey[500],
    complete: colors.green[500],
    failed: colors.red[500],
    idle: colors.grey[500],
  }[phase];

  const label = {
    running: "Training locally",
    aggregating: "Aggregating",
    paused: "Paused",
    complete: "Complete",
    failed: "Failed",
    idle: "Idle",
  }[phase];

  const clientCount = Math.min(Math.max(Number(connectedClients) || 0, 0), 8);
  const ghost = clientCount === 0; // active but no clients reported yet
  const displayCount = ghost ? 4 : clientCount;
  const clients = useMemo(() => layoutClients(displayCount), [displayCount]);

  const pct = Math.max(0, Math.min(100, Math.round(Number(progress) || 0)));
  const roundsKnown = Number(totalRounds) > 0;
  const roundNow = Math.min(Number(currentRound) || 0, Number(totalRounds) || 0);
  const segments = roundsKnown && totalRounds <= 24 ? Number(totalRounds) : 0;

  const subtitle = ghost
    ? "Establishing secure client connections…"
    : `${displayCount} client${displayCount === 1 ? "" : "s"} · ${
        roundsKnown ? `round ${roundNow}/${totalRounds}` : "federated round"
      }`;

  // colors used for node structure
  const nodeStroke =
    phase === "complete" ? colors.green[400] : phase === "failed" ? colors.red[300] : colors.grey[700];
  const nodeIcon =
    phase === "complete" ? colors.green[600] : phase === "failed" ? colors.red[400] : colors.secondary[600];
  const edgeBase = phase === "failed" ? colors.red[200] : colors.grey[800];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 300, opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.25, ease: "easeInOut" }}
          style={{
            overflow: "hidden",
            marginBottom: 16,
            borderRadius: 8,
            boxShadow: "var(--fx-shadow-sm)",
            border: "1px solid var(--fx-border)",
            backgroundColor: "var(--fx-paper)",
          }}
          role="status"
          aria-live="polite"
          aria-label={`Federated training ${label}${
            roundsKnown ? `, round ${roundNow} of ${totalRounds}` : ""
          }, ${pct} percent`}
        >
          {/* Header */}
          <Box
            sx={{
              pt: 1.75,
              px: 2,
              pb: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  color: 'var(--fx-text-primary)',
                  fontSize: "0.9375rem",
                  letterSpacing: "-0.005em",
                }}
              >
                Federated training
              </Typography>
              <Typography
                sx={{
                  color: colors.grey[500],
                  fontSize: "0.75rem",
                  mt: 0.25,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {subtitle}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.4,
                borderRadius: 999,
                flexShrink: 0,
                bgcolor: `${accent}1A`,
                color: accent,
                fontFamily: "var(--fx-font-mono)",
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: accent,
                  boxShadow: `0 0 8px ${accent}B3`,
                  animation: live ? "fxStatusBlink 1.4s ease-in-out infinite" : "none",
                }}
              />
              {label.toUpperCase()} · {pct}%
            </Box>
          </Box>

          {/* Stage */}
          <Box sx={{ position: "relative", width: "100%", height: 200 }}>
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 800 224"
              preserveAspectRatio="xMidYMid meet"
              aria-hidden="true"
            >
              <defs>
                <radialGradient id="fxHubGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor={accent} stopOpacity="0.4" />
                  <stop offset="70%" stopColor={accent} stopOpacity="0.06" />
                  <stop offset="100%" stopColor={accent} stopOpacity="0" />
                </radialGradient>
                <linearGradient id="fxHubFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3a6f97" />
                  <stop offset="50%" stopColor="#23456b" />
                  <stop offset="100%" stopColor="#16304f" />
                </linearGradient>
                <linearGradient id="fxHubGloss" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--fx-paper)" stopOpacity="0.28" />
                  <stop offset="100%" stopColor="var(--fx-paper)" stopOpacity="0" />
                </linearGradient>
                <filter id="fxGlow" x="-80%" y="-80%" width="260%" height="260%">
                  <feGaussianBlur stdDeviation="3.2" />
                </filter>
                <filter id="fxCardShadow" x="-40%" y="-40%" width="180%" height="180%">
                  <feDropShadow dx="0" dy="1.5" stdDeviation="2" floodColor="var(--fx-text-strong)" floodOpacity="0.12" />
                </filter>
                <style>{`
                  @keyframes fxFlow    { to { stroke-dashoffset: -160; } }
                  @keyframes fxFlowOut { to { stroke-dashoffset:  160; } }
                  @keyframes fxPacket {
                    0%   { transform: translate(0,0); opacity: 0; }
                    12%  { opacity: 1; }
                    85%  { opacity: 1; }
                    100% { transform: translate(var(--fx-dx), var(--fx-dy)); opacity: 0; }
                  }
                  @keyframes fxNodePulse {
                    0%, 100% { transform: scale(1); }
                    50%      { transform: scale(1.12); }
                  }
                  @keyframes fxAggRing {
                    0%   { transform: scale(1);   opacity: 0.5; }
                    100% { transform: scale(5.2); opacity: 0; }
                  }
                  @keyframes fxHubBreathe {
                    0%, 100% { opacity: 0.85; }
                    50%      { opacity: 1; }
                  }
                  @keyframes fxStatusBlink { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
                  @keyframes fxLed { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
                `}</style>
              </defs>

              {/* ── Edges: base hairline + animated flow overlay ── */}
              {clients.map((c, i) => (
                <line
                  key={`edge-${i}`}
                  x1={c.x}
                  y1={c.y}
                  x2={HUB.x}
                  y2={HUB.y}
                  stroke={edgeBase}
                  strokeWidth={1}
                  opacity={ghost ? 0.55 : 0.8}
                />
              ))}
              {live &&
                clients.map((c, i) => (
                  <line
                    key={`flow-${i}`}
                    x1={c.x}
                    y1={c.y}
                    x2={HUB.x}
                    y2={HUB.y}
                    stroke={accent}
                    strokeWidth={1.5}
                    strokeLinecap="round"
                    strokeDasharray="2 11"
                    opacity={ghost ? 0.45 : 0.85}
                    style={{
                      animation: `${
                        phase === "aggregating" ? "fxFlowOut" : "fxFlow"
                      } 2.6s linear ${(i * 0.12).toFixed(2)}s infinite`,
                    }}
                  />
                ))}

              {/* ── Glowing update packets (client → server) ── */}
              {live &&
                !ghost &&
                clients.map((c, i) => {
                  const dx = HUB.x - c.x;
                  const dy = HUB.y - c.y;
                  const start = phase === "aggregating" ? { x: HUB.x, y: HUB.y } : { x: c.x, y: c.y };
                  const vx = phase === "aggregating" ? -dx : dx;
                  const vy = phase === "aggregating" ? -dy : dy;
                  return (
                    <g key={`pkt-${i}`}>
                      <circle
                        cx={start.x}
                        cy={start.y}
                        r={5}
                        fill={accent}
                        filter="url(#fxGlow)"
                        opacity={0.7}
                        style={{
                          "--fx-dx": `${vx}px`,
                          "--fx-dy": `${vy}px`,
                          animation: `fxPacket 2.2s ease-in-out ${(i * 0.26).toFixed(2)}s infinite`,
                        }}
                      />
                      <circle
                        cx={start.x}
                        cy={start.y}
                        r={2.4}
                        fill="var(--fx-paper)"
                        style={{
                          "--fx-dx": `${vx}px`,
                          "--fx-dy": `${vy}px`,
                          animation: `fxPacket 2.2s ease-in-out ${(i * 0.26).toFixed(2)}s infinite`,
                        }}
                      />
                    </g>
                  );
                })}

              {/* ── Client nodes: a device holding LOCAL DATA (db cylinder) ── */}
              {clients.map((c, i) => (
                <g
                  key={`node-${i}`}
                  style={{
                    transformBox: "fill-box",
                    transformOrigin: "center",
                    transform: "translateZ(0)",
                  }}
                >
                  <g
                    style={{
                      transformOrigin: `${c.x}px ${c.y}px`,
                      animation:
                        live && phase === "running" && !ghost
                          ? `fxNodePulse 2.6s ease-in-out ${(i * 0.32).toFixed(2)}s infinite`
                          : "none",
                    }}
                  >
                    <rect
                      x={c.x - 16}
                      y={c.y - 15}
                      width={32}
                      height={30}
                      rx={7}
                      fill="var(--fx-paper)"
                      stroke={nodeStroke}
                      strokeWidth={1.5}
                      opacity={ghost ? 0.65 : 1}
                      filter="url(#fxCardShadow)"
                    />
                    {/* database cylinder = data stays on the client */}
                    <g
                      stroke={nodeIcon}
                      strokeWidth={1.4}
                      fill="none"
                      opacity={ghost ? 0.55 : 1}
                      strokeLinecap="round"
                    >
                      <ellipse cx={c.x} cy={c.y - 6} rx={7} ry={2.6} fill={`${nodeIcon}22`} />
                      <path d={`M ${c.x - 7} ${c.y - 6} V ${c.y + 5}`} />
                      <path d={`M ${c.x + 7} ${c.y - 6} V ${c.y + 5}`} />
                      <path d={`M ${c.x - 7} ${c.y + 5} A 7 2.6 0 0 0 ${c.x + 7} ${c.y + 5}`} />
                      <path d={`M ${c.x - 7} ${c.y - 0.5} A 7 2.6 0 0 0 ${c.x + 7} ${c.y - 0.5}`} />
                    </g>
                    {/* status LED */}
                    <circle
                      cx={c.x + 11}
                      cy={c.y - 10}
                      r={2.2}
                      fill={ghost ? colors.grey[500] : accent}
                      style={{
                        animation: live && !ghost ? `fxLed 1.6s ease-in-out ${(i * 0.2).toFixed(2)}s infinite` : "none",
                      }}
                    />
                  </g>
                </g>
              ))}

              {/* ── Aggregation / redistribution ring — replays each round ── */}
              {live && phase === "aggregating" && (
                <circle
                  key={`agg-${currentRound}`}
                  cx={HUB.x}
                  cy={HUB.y}
                  r={36}
                  fill="none"
                  stroke={colors.amber[500]}
                  strokeWidth={2}
                  style={{
                    transformBox: "fill-box",
                    transformOrigin: "center",
                    animation: "fxAggRing 1.1s ease-out forwards",
                  }}
                />
              )}

              {/* ── Server / aggregator hub ── */}
              <circle
                cx={HUB.x}
                cy={HUB.y}
                r={60}
                fill="url(#fxHubGlow)"
                style={{ animation: live ? "fxHubBreathe 3s ease-in-out infinite" : "none" }}
              />
              <rect
                x={HUB.x - 34}
                y={HUB.y - 34}
                width={68}
                height={68}
                rx={16}
                fill="url(#fxHubFill)"
                stroke={accent}
                strokeWidth={2}
                filter="url(#fxCardShadow)"
              />
              {/* glossy top highlight for a lighter, dimensional face */}
              <rect
                x={HUB.x - 31}
                y={HUB.y - 31}
                width={62}
                height={30}
                rx={13}
                fill="url(#fxHubGloss)"
                style={{ pointerEvents: "none" }}
              />
              {/* inner accent rim */}
              <rect
                x={HUB.x - 29}
                y={HUB.y - 29}
                width={58}
                height={58}
                rx={12}
                fill="none"
                stroke="rgba(125,211,252,0.40)"
                strokeWidth={1}
                style={{ pointerEvents: "none" }}
              />
              {/* server rack: three slots, each with an LED */}
              <g>
                {[-17, 0, 17].map((dy, k) => (
                  <g key={`rack-${k}`}>
                    <rect
                      x={HUB.x - 23}
                      y={HUB.y + dy - 5}
                      width={46}
                      height={10}
                      rx={3}
                      fill="rgba(255,255,255,0.12)"
                      stroke="rgba(255,255,255,0.22)"
                      strokeWidth={1}
                    />
                    <circle
                      cx={HUB.x - 16}
                      cy={HUB.y + dy}
                      r={2}
                      fill={phase === "failed" ? colors.red[300] : phase === "complete" ? colors.green[300] : "#7dd3fc"}
                      style={{
                        animation: live ? `fxLed 1.3s ease-in-out ${(k * 0.25).toFixed(2)}s infinite` : "none",
                      }}
                    />
                    <line
                      x1={HUB.x - 9}
                      y1={HUB.y + dy}
                      x2={HUB.x + 17}
                      y2={HUB.y + dy}
                      stroke="rgba(255,255,255,0.35)"
                      strokeWidth={1.4}
                      strokeLinecap="round"
                    />
                  </g>
                ))}
              </g>

              {/* Terminal badges on the hub */}
              {phase === "complete" && (
                <g>
                  <circle cx={HUB.x} cy={HUB.y} r={20} fill={colors.green[500]} />
                  <path
                    d={`M ${HUB.x - 9} ${HUB.y} l 6 6 l 12 -13`}
                    fill="none"
                    stroke="var(--fx-paper)"
                    strokeWidth={3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              )}
              {phase === "failed" && (
                <g>
                  <circle cx={HUB.x} cy={HUB.y} r={20} fill={colors.red[500]} />
                  <g stroke="var(--fx-paper)" strokeWidth={3} strokeLinecap="round">
                    <line x1={HUB.x - 7} y1={HUB.y - 7} x2={HUB.x + 7} y2={HUB.y + 7} />
                    <line x1={HUB.x + 7} y1={HUB.y - 7} x2={HUB.x - 7} y2={HUB.y + 7} />
                  </g>
                </g>
              )}

              {/* ── Round-progress track ── */}
              {segments > 0 ? (
                <g transform="translate(0, 212)">
                  {Array.from({ length: segments }).map((_, i) => {
                    const gap = 4;
                    const usable = 720;
                    const segW = (usable - gap * (segments - 1)) / segments;
                    const x = 40 + i * (segW + gap);
                    const done = i < roundNow;
                    const active = i === roundNow && phase !== "complete";
                    return (
                      <rect
                        key={`seg-${i}`}
                        x={x}
                        y={0}
                        width={segW}
                        height={4}
                        rx={2}
                        fill={
                          phase === "failed" && active
                            ? colors.red[400]
                            : done || phase === "complete"
                            ? colors.green[500]
                            : active
                            ? accent
                            : colors.grey[800]
                        }
                      />
                    );
                  })}
                </g>
              ) : (
                <g transform="translate(0, 212)">
                  <rect x={40} y={0} width={720} height={4} rx={2} fill={colors.grey[800]} />
                  <rect
                    x={40}
                    y={0}
                    width={(720 * pct) / 100}
                    height={4}
                    rx={2}
                    fill={phase === "failed" ? colors.red[500] : phase === "complete" ? colors.green[500] : accent}
                  />
                </g>
              )}
            </svg>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TrainingAnimation;
